import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Plus, Minus, Star, ShoppingBag, Ruler, Sparkles, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { money, SIZE_BY_CODE } from "@/lib/orderStatus";
import { ProductGallery } from "@/components/product/ProductGallery";
import { ProductAccordions } from "@/components/product/ProductAccordions";
import { SuggestionsRow } from "@/components/product/SuggestionsRow";

export default function ProductDetailSheet({ product: p, currency, slug, inCart = 0, reviews, suggestions = [], onAddQty, onClose, onOpen }) {
  const { t } = useTranslation();
  // Galerie : uniquement les photos nettoyées (clean) ; repli sur la version brute si non nettoyée.
  // Compat anciens produits : packshot_url puis images puis image_url.
  const photoUrls = (p.photos && p.photos.length)
    ? p.photos.map((ph) => ph.clean || ph.raw).filter(Boolean)
    : (p.packshot_url ? [p.packshot_url] : (p.images?.length ? p.images : (p.image_url ? [p.image_url] : [])));
  const gallery = photoUrls.map((u) => ({ url: u }));

  const sizes = p.sizes || [];
  const needSize = sizes.length > 0;
  const sizeStock = p.size_stock || {};
  const hasSizeStock = Object.keys(sizeStock).length > 0;

  const [mainIdx, setMainIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState("");
  const [showChart, setShowChart] = useState(false);
  const [aiBySize, setAiBySize] = useState({});
  const [aiBusy, setAiBusy] = useState(false);
  const [viewAI, setViewAI] = useState(false);

  const rating = p.popularity || 0;
  const out = p.stock_qty <= 0;

  const aiSrc = viewAI && size ? aiBySize[size] : null;
  const main = gallery[mainIdx] || gallery[0];
  const displaySrc = aiSrc || main?.url;
  const displayIsAI = !!aiSrc;

  const sizeOut = (c) => needSize && hasSizeStock && (sizeStock[c] || 0) <= 0;
  const sizeMax = size ? (sizeStock[size] != null ? sizeStock[size] : p.stock_qty) : p.stock_qty;
  const maxAdd = Math.max(0, sizeMax);

  const pickSize = (c) => { if (sizeOut(c)) return; setSize(c); setViewAI(false); };
  const clickThumb = (i) => { setMainIdx(i); setViewAI(false); };

  // Aperçu IA « porté » à la demande de l'acheteur (généré une fois puis mis en cache côté serveur).
  const requestAiPreview = async () => {
    if (!size) { toast.error(t("detail.chooseSizeFirst")); return; }
    if (aiBySize[size]) { setViewAI(true); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/public/store/${slug}/ai-preview`, { product_id: p.product_id, size });
      setAiBySize((m) => ({ ...m, [size]: data.image }));
      setViewAI(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("detail.aiError"));
    } finally {
      setAiBusy(false);
    }
  };

  const addToCart = () => {
    if (needSize && !size) return;
    onAddQty?.(p, qty, size);
    setSize("");
    setViewAI(false);
    setQty(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 overflow-y-auto"
      onClick={onClose}
      data-testid="product-detail-sheet"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="min-h-screen md:min-h-0 md:my-8 bg-white w-full md:max-w-5xl mx-auto md:rounded-sm"
      >
        <div className="sticky top-0 z-10 flex justify-end p-3 bg-white/90 backdrop-blur-sm md:bg-transparent">
          <button data-testid="close-detail-btn" onClick={onClose} className="h-10 w-10 rounded-full bg-[#F3F3F3] flex items-center justify-center hover:bg-[#E5E5E5] transition-colors"><X className="h-5 w-5" /></button>
        </div>

        <div className="md:grid md:grid-cols-2 md:gap-10 px-5 md:px-10 pb-10 -mt-2">
          <div className="relative">
            <ProductGallery
              gallery={gallery}
              displaySrc={displaySrc}
              displayIsAI={displayIsAI}
              size={size}
              mainIdx={mainIdx}
              name={p.name}
              onThumb={clickThumb}
            />
            {aiBusy && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                data-testid="ai-preview-loading"
                className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/80 backdrop-blur-[3px]"
              >
                <div className="relative flex items-center justify-center">
                  <Loader2 className="h-9 w-9 text-[#1A1A1A] animate-spin" />
                  <Sparkles className="h-4 w-4 text-[#1A1A1A] absolute animate-pulse" />
                </div>
                <p className="font-serif-display text-lg text-[#1A1A1A] mt-4 text-center px-6" data-testid="ai-preview-loading-title">{t("detail.aiGenerating")}</p>
                <p className="font-jost text-xs text-[#999] mt-2 max-w-[240px] text-center px-6 leading-relaxed">{t("detail.aiGeneratingHint")}</p>
                <div className="mt-4 h-1 w-40 overflow-hidden rounded-full bg-[#ECECEC]">
                  <motion.div
                    className="h-full w-1/3 bg-[#1A1A1A] rounded-full"
                    animate={{ x: ["-140%", "300%"] }}
                    transition={{ duration: 1.3, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
              </motion.div>
            )}
          </div>

          {/* Infos */}
          <div className="mt-6 md:mt-0">
            <h1 className="font-serif-display text-3xl md:text-4xl font-medium text-[#1A1A1A] leading-tight" data-testid="detail-name">{p.name}</h1>
            <div className="flex items-center gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-3.5 w-3.5 ${rating >= n ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#D9D9D9]"}`} />
              ))}
              {reviews?.count > 0 && <span className="font-jost text-xs text-[#666] ml-1">({reviews.count} avis)</span>}
            </div>
            <p className="font-jost text-2xl text-[#1A1A1A] mt-4" data-testid="detail-price">{money(p.price, currency)}</p>

            {p.description && (
              <p className="font-jost text-sm text-[#666] leading-relaxed mt-4 line-clamp-4">{p.description}</p>
            )}

            {/* Sélection de la taille */}
            {needSize && (
              <div className="mt-6" data-testid="size-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-jost text-xs uppercase tracking-widest text-[#1A1A1A]">{t("detail.chooseSize")}</p>
                  <button data-testid="size-chart-btn" onClick={() => setShowChart((v) => !v)} className="font-jost text-xs text-[#666] underline inline-flex items-center gap-1">
                    <Ruler className="h-3.5 w-3.5" /> {t("detail.sizeChart")}
                  </button>
                </div>
                {showChart && (
                  <div className="mb-3 border border-[#E5E5E5] rounded-sm overflow-hidden" data-testid="size-chart">
                    {sizes.map((c) => {
                      const s = SIZE_BY_CODE[c];
                      return s ? (
                        <div key={c} className="flex justify-between px-3 py-2 text-sm border-b border-[#F0F0F0] last:border-0">
                          <span className="text-[#1A1A1A]">{s.code} <span className="text-[#999] font-semibold">({s.en})</span></span>
                          <span className="text-[#666]">{t("detail.breastTour", { cm: s.cm })}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  {sizes.map((c) => {
                    const s = SIZE_BY_CODE[c] || { code: c, en: c };
                    const on = size === c;
                    const oos = sizeOut(c);
                    return (
                      <button
                        key={c}
                        data-testid={`size-option-${c}`}
                        onClick={() => pickSize(c)}
                        disabled={oos}
                        className={`w-full text-left px-4 py-3 border transition-colors flex items-center justify-between ${oos ? "border-[#E5E5E5] text-[#BBB] line-through cursor-not-allowed" : on ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#D9D9D9] text-[#1A1A1A] hover:border-[#1A1A1A]"}`}
                      >
                        <span><span className="font-jost text-sm font-semibold">{s.code}</span> <span className={`text-xs ${on ? "text-white/70" : "text-[#999]"}`}>({s.en})</span></span>
                        {oos && <span className="text-[10px] uppercase tracking-wide" data-testid={`size-oos-${c}`}>{t("store.soldOut")}</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Aperçu IA optionnel — à la demande de l'acheteur */}
                <div className="mt-3">
                  {!viewAI ? (
                    <button
                      data-testid="ai-preview-btn"
                      onClick={requestAiPreview}
                      disabled={!size || aiBusy}
                      className="w-full h-11 border border-[#1A1A1A] text-[#1A1A1A] font-jost text-xs uppercase tracking-widest hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {aiBusy ? t("detail.aiGenerating") : t("detail.aiBtn")}
                    </button>
                  ) : (
                    <button
                      data-testid="ai-preview-reset-btn"
                      onClick={() => setViewAI(false)}
                      className="w-full h-11 border border-[#D9D9D9] text-[#666] font-jost text-xs uppercase tracking-widest hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="h-4 w-4" /> {t("detail.aiReset")}
                    </button>
                  )}
                  <p className="font-jost text-[11px] text-[#999] mt-1.5 text-center">{t("detail.aiHint")}</p>
                </div>
              </div>
            )}

            {/* Quantité */}
            <div className="mt-6">
              <p className="font-jost text-xs uppercase tracking-widest text-[#1A1A1A] mb-2">{t("detail.quantity")}</p>
              <div className="inline-flex items-center border border-[#1A1A1A]">
                <button data-testid="detail-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-12 flex items-center justify-center hover:bg-[#F3F3F3]"><Minus className="h-4 w-4" /></button>
                <span className="w-12 text-center font-jost" data-testid="detail-qty">{qty}</span>
                <button data-testid="detail-qty-plus" onClick={() => setQty((q) => Math.min(Math.max(1, maxAdd || 1), q + 1))} className="h-11 w-12 flex items-center justify-center hover:bg-[#F3F3F3]"><Plus className="h-4 w-4" /></button>
              </div>
              {inCart > 0 && <p className="font-jost text-xs text-[#666] mt-2" data-testid="detail-incart">{t("detail.alreadyInCart", { count: inCart })}</p>}
            </div>

            <button
              data-testid="product-add-to-cart"
              disabled={out || maxAdd <= 0 || (needSize && !size)}
              onClick={addToCart}
              className="w-full h-14 mt-6 bg-black text-white font-jost text-sm uppercase tracking-[0.2em] hover:bg-[#333] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ShoppingBag className="h-4 w-4" /> {out ? t("detail.soldOut") : maxAdd <= 0 ? t("detail.maxReached") : (needSize && !size) ? t("detail.chooseSizeBtn") : t("detail.addToCart")}
            </button>

            <ProductAccordions p={p} reviews={reviews} out={out} />
          </div>
        </div>

        <SuggestionsRow suggestions={suggestions} currency={currency} onOpen={onOpen} />
      </motion.div>
    </motion.div>
  );
}
